import React, { useState } from 'react';
import { Navigate, Link, useLocation, Outlet } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useTenant from '../../hooks/useTenant';

export const ProtectedLayout: React.FC = () => {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const { tenantName, logoUrl } = useTenant();
  const location = useLocation();

  // Navigation states
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-xs font-semibold text-slate-500 text-helper">
            Securing portal context...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const getNavItems = () => {
    const items = [
      { 
        label: 'Dashboard', 
        path: '/', 
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4zM14 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2v-4z" />
          </svg>
        )
      }
    ];

    const permissions = user?.permissions || [];
    const roles = user?.roles || [];
    const hasPermission = (p: string) => permissions.includes(p);
    const hasRole = (r: string) => roles.includes(r);

    if (hasPermission('manage_teachers')) {
      items.push({ 
        label: 'Teachers', 
        path: '/teachers', 
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        )
      });
    }

    if (hasPermission('assign_classes')) {
      items.push({ 
        label: 'Manage Classes', 
        path: '/manage-classes', 
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        )
      });
    }

    if (hasPermission('manage_subjects')) {
      items.push({ 
        label: 'Subjects', 
        path: '/subjects', 
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        )
      });
    }

    if (hasRole('headteacher') || hasRole('school_admin') || hasRole('deputy_headteacher')) {
      items.push({ 
        label: 'Reports & Analytics', 
        path: '/reports', 
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        )
      });
    }

    if (hasRole('teacher') || hasRole('class_teacher')) {
      items.push({ 
        label: 'My Classes', 
        path: '/classes', 
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        )
      });
      // Teachers can also view reports
      if (!items.some(i => i.path === '/reports')) {
        items.push({
          label: 'Reports',
          path: '/reports',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          )
        });
      }
    }

    items.push({
      label: 'My Profile',
      path: '/profile',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    });

    return items;
  };
  
  const navItems = getNavItems();

  const initials = user 
    ? `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase() 
    : 'U';

  const currentNav = navItems.find(item => 
    location.pathname === item.path || 
    (item.path !== '/' && location.pathname.startsWith(item.path))
  );
  const breadcrumbLabel = currentNav ? currentNav.label : 'Application';

  // Format user roles for display
  const userRolesDisplay = user?.roles 
    ? user.roles.map((r: string) => r.replace(/_/g, ' ')).join(', ') 
    : 'User';

  // Sidebar link rendering helper
  const renderSidebarLinks = (onClickCallback?: () => void) => {
    return navItems.map((item) => {
      const active = location.pathname === item.path || 
        (item.path !== '/' && location.pathname.startsWith(item.path));
      
      return (
        <Link
          key={item.label}
          to={item.path}
          onClick={onClickCallback}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-nav ${
            active 
              ? 'bg-indigo-600 text-white font-semibold shadow-md' 
              : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          <span className="shrink-0">{item.icon}</span>
          <span className={`transition-opacity duration-200 ${!sidebarExpanded ? 'lg:hidden' : ''}`}>
            {item.label}
          </span>
        </Link>
      );
    });
  };

  // Determine mobile bottom nav links dynamically based on role
  const getBottomNavItems = () => {
    const items = [
      { label: 'Home', path: '/', icon: <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg> }
    ];

    const roles = user?.roles || [];
    if (roles.includes('school_admin')) {
      items.push({ label: 'Classes', path: '/manage-classes', icon: <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg> });
    } else if (roles.includes('teacher') || roles.includes('class_teacher')) {
      items.push({ label: 'Classes', path: '/classes', icon: <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg> });
    }

    items.push({ label: 'Reports', path: '/reports', icon: <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 2v-6m-9 9h12a2 2 0 002-2V9a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> });
    items.push({ label: 'Profile', path: '/profile', icon: <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg> });

    return items;
  };

  const bottomNavItems = getBottomNavItems();

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-50 text-slate-900">
      
      {/* 1. Desktop Sidebar Navigation (Hidden on Mobile) */}
      <aside className={`hidden md:flex flex-col bg-slate-900 text-white shrink-0 shadow-lg border-r border-slate-800 transition-all duration-200 ${
        sidebarExpanded ? 'w-64' : 'w-20'
      }`}>
        {/* Sidebar Header */}
        <div className="flex items-center gap-3 h-16 px-4 border-b border-slate-800 shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt={tenantName} className="w-9 h-9 rounded-lg bg-white p-0.5 object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-lg font-bold">🏫</div>
          )}
          <div className={`overflow-hidden transition-all duration-200 ${sidebarExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0 pointer-events-none'}`}>
            <h2 className="font-bold text-xs leading-tight text-white truncate max-w-[140px]">{tenantName}</h2>
            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Portal</span>
          </div>
        </div>

        {/* Sidebar Navigation Scroll Area */}
        <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1.5 scrollbar-thin scrollbar-thumb-slate-800">
          {renderSidebarLinks()}
        </nav>

        {/* Sidebar Footer Controls */}
        <div className="p-3 border-t border-slate-800 space-y-1">
          <button 
            onClick={() => setSidebarExpanded(!sidebarExpanded)} 
            className="w-full flex items-center justify-center py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all text-helper font-medium gap-2"
          >
            <svg className={`w-4 h-4 transition-transform duration-200 ${sidebarExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
            <span className={sidebarExpanded ? 'inline' : 'hidden'}>Collapse Sidebar</span>
          </button>
          
          <button 
            onClick={logout} 
            className="w-full flex items-center py-2.5 px-3 rounded-lg text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition-all text-helper font-medium gap-2"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className={sidebarExpanded ? 'inline' : 'hidden'}>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* 2. Mobile Drawer Navigation Overlay (Responsive < 768px Drawer) */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setMobileDrawerOpen(false)}>
          <aside className="w-64 max-w-[80vw] h-full bg-slate-900 text-white flex flex-col justify-between py-6 shadow-2xl animate-slide-in" onClick={e => e.stopPropagation()}>
            <div>
              <div className="flex items-center gap-3 px-5 mb-6 border-b border-slate-800 pb-5">
                {logoUrl ? (
                  <img src={logoUrl} alt={tenantName} className="w-9 h-9 rounded-lg bg-white p-0.5 object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-lg font-bold">🏫</div>
                )}
                <div>
                  <h2 className="font-bold text-xs text-white leading-tight truncate max-w-[130px]">{tenantName}</h2>
                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Mobile Portal</span>
                </div>
              </div>
              <nav className="flex flex-col gap-1.5 px-3">
                {renderSidebarLinks(() => setMobileDrawerOpen(false))}
              </nav>
            </div>
            <div className="px-5 border-t border-slate-800 pt-5">
              <button 
                onClick={() => { setMobileDrawerOpen(false); logout(); }} 
                className="w-full flex items-center gap-3 py-3 text-rose-400 font-semibold text-helper"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Sign Out</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* 3. Main Interface Panel Workspace */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        
        {/* Sticky Header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 h-16 px-4 md:px-8 flex items-center justify-between shadow-sm shrink-0">
          
          {/* Mobile hamburger & Breadcrumbs */}
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMobileDrawerOpen(true)} 
              className="md:hidden p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div className="flex items-center gap-2 text-helper font-semibold text-slate-400">
              <span className="hidden sm:inline">Application</span>
              <span className="hidden sm:inline text-slate-300">/</span>
              <span className="text-slate-800 font-bold">{breadcrumbLabel}</span>
            </div>
          </div>

          {/* User Details & Profile Avatar Row */}
          <div className="flex items-center gap-4">
            
            {/* Search Input (Hidden on Mobile) */}
            <div className="relative hidden lg:block">
              <input 
                type="text" 
                placeholder="Search..." 
                className="w-48 bg-slate-100 border-0 rounded-full pl-9 pr-4 py-1.5 text-helper focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition-all outline-none"
              />
              <svg className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Notification Bell */}
            <button className="text-slate-400 hover:text-slate-600 relative p-1.5 rounded-full hover:bg-slate-100 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full"></span>
            </button>
            
            {/* User Meta Row */}
            <div className="flex items-center gap-2.5">
              <div className="text-right hidden sm:block">
                <p className="font-bold text-helper text-slate-800 leading-tight">{user?.first_name} {user?.last_name}</p>
                <span className="text-[9px] text-indigo-600 font-bold uppercase tracking-wider leading-none">
                  {userRolesDisplay}
                </span>
              </div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center font-bold text-helper border-2 border-white shadow-md">
                {initials}
              </div>
            </div>

          </div>
        </header>

        {/* Main App Content Body (Adjust padding bottom on mobile for navigation bar padding) */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8 pb-20 md:pb-8 bg-slate-50">
          <Outlet />
        </main>

        {/* Mobile Floating Action Button (FAB) and Shortcuts Menu */}
        <div className="fixed bottom-20 right-4 z-40 md:hidden">
          {fabOpen && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-4 mb-3 w-48 space-y-2 animate-fade-in-up">
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block border-b pb-1.5 mb-2">Quick Actions</span>
              <Link 
                to="/classes" 
                onClick={() => setFabOpen(false)}
                className="flex items-center gap-2.5 text-nav font-bold text-slate-700 hover:text-indigo-600 transition-colors py-1 block"
              >
                <span>✏️</span> Enter Scores
              </Link>
              <Link 
                to="/reports" 
                onClick={() => setFabOpen(false)}
                className="flex items-center gap-2.5 text-nav font-bold text-slate-700 hover:text-indigo-600 transition-colors py-1 block"
              >
                <span>📋</span> View Reports
              </Link>
              <Link 
                to="/profile" 
                onClick={() => setFabOpen(false)}
                className="flex items-center gap-2.5 text-nav font-bold text-slate-700 hover:text-indigo-600 transition-colors py-1 block"
              >
                <span>👤</span> Account Settings
              </Link>
            </div>
          )}
          <button 
            onClick={() => setFabOpen(!fabOpen)}
            className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-lg hover:bg-indigo-700 active:scale-95 transition-all text-xl focus:outline-none"
            aria-label="Quick Actions"
          >
            {fabOpen ? '✕' : '⚡'}
          </button>
        </div>

        {/* 4. Mobile Sticky Bottom Navigation Bar (Visible < 768px Viewports) */}
        <nav className="fixed bottom-0 left-0 right-0 z-30 md:hidden bg-white border-t border-slate-200 h-16 flex items-center justify-around px-2 shadow-lg">
          {bottomNavItems.map((item) => {
            const active = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.label}
                to={item.path}
                className={`flex flex-col items-center justify-center w-16 h-12 rounded-lg transition-colors ${
                  active 
                    ? 'text-indigo-600 font-bold' 
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <span>{item.icon}</span>
                <span className="text-[9px] mt-0.5 font-medium tracking-wide leading-none">{item.label}</span>
              </Link>
            );
          })}
        </nav>

      </div>

    </div>
  );
};

export default ProtectedLayout;
